import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { formatCurrency } from '../../../utils/formatters';
import transactionService from '../services/transactionService';
import categoryService from '../services/categoryService';
import smartCategorizationService from '../services/smartCategorizationService';
import userPreferencesService from '../services/userPreferencesService';
import TransactionOwnerTag from './TransactionOwnerTag';
import CategoryTag from './CategoryTag';
import CategorySelector from './CategorySelector';
import ColumnCustomizationModal from './ColumnCustomizationModal';
import ContextMenu from './ContextMenu';
import toast from 'react-hot-toast';
import { 
  SparklesIcon, 
  BanknotesIcon, 
  ClockIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  CreditCardIcon,
  ArrowPathIcon,
  TagIcon,
  ListBulletIcon,
  ArrowPathRoundedSquareIcon,
  BuildingStorefrontIcon,
  UserIcon,
  Cog6ToothIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import TypeSelector from './TypeSelector';
import OwnerSelector from './OwnerSelector';
import BulkEditModal from './BulkEditModal';
import axios from 'axios';
import { useAuthContext } from '../../auth/context/AuthContext';

const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const errorHandler = (error) => {
      console.error('Caught error in ErrorBoundary:', error);
      setHasError(true);
    };

    window.addEventListener('error', errorHandler);
    
    return () => {
      window.removeEventListener('error', errorHandler);
    };
  }, []);

  if (hasError) {
    return (
      <div className="text-center p-8 bg-red-50 rounded-lg">
        <p className="text-red-600 font-semibold mb-2">Something went wrong.</p>
        <p className="text-gray-600">
          Please try refreshing the page or contact support if the issue persists.
        </p>
        <button 
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

const TransactionList = ({ 
  transactions = [], 
  accounts, 
  accountId, 
  navigate,
  onTransactionUpdate,
  filters = {},
  onSortChange = null,
  onPageChange = null,
  onFilterClick = null,
  selectedTransactionIds: externalSelectedIds = [],
  onSelectionChange,
  onColumnCustomizeClick = null,
  onFilteredTransactionsChange = null, // Callback to pass filtered transactions to parent
  onFilterChange = null // Callback to update filters from context menu
}) => {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [categories, setCategories] = useState([]);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [autoCategorizing, setAutoCategorizing] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editingOwnerId, setEditingOwnerId] = useState(null);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState(externalSelectedIds || []);
  const [selectAll, setSelectAll] = useState(false);
  const [columnPreferences, setColumnPreferences] = useState(null);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    type: null, // 'header' or 'cell'
    data: null // column info or cell data
  });
  const [expandedDescriptionId, setExpandedDescriptionId] = useState(null); // Track which description is expanded

  // Get user context to check for partnership
  const { user } = useAuthContext();
  
  // Check if user has a partnership (for showing Shared column)
  const hasPartnership = useMemo(() => {
    return !!(user && (user.partnerId || user.partner || user.createdPartner));
  }, [user]);

  // Sync with external selection IDs when they change
  useEffect(() => {
    if (Array.isArray(externalSelectedIds) && externalSelectedIds.length > 0) {
      setSelectedTransactionIds(externalSelectedIds);
      setSelectAll(
        externalSelectedIds.length > 0 && 
        transactions.length > 0 && 
        externalSelectedIds.length === transactions.length
      );
    }
  }, [externalSelectedIds, transactions.length]);

  // Helper to detect if a transaction is from Plaid - moved up before it's used in sorting
  const isPlaidTransaction = (transaction) => {
    return !!(
      (transaction.metadata && transaction.metadata.source === 'plaid') ||
      (transaction.metadata && transaction.metadata.plaidTransactionId) || 
      (transaction.uniqueId && typeof transaction.uniqueId === 'string' && transaction.uniqueId.startsWith('plaid_'))
    );
  };
  
  // Helper to get institution name for Plaid transactions - moved up before it's used in sorting
  const getInstitutionName = (transaction) => {
    if (transaction.metadata?.institutionName) {
      return transaction.metadata.institutionName;
    }
    
    if (transaction.metadata?.original?.institutionName) {
      return transaction.metadata.original.institutionName;
    }
    
    if (transaction.wallet?.name && transaction.wallet.name !== 'Unknown') {
      return transaction.wallet.name;
    }
    
    return 'Bank Account';
  };
  
  // Get category name for sorting
  const getCategoryName = (transaction) => {
    if (transaction.categoryRef?.name) {
      return transaction.categoryRef.name;
    }
    if (transaction.category?.name) {
      return transaction.category.name;
    }
    return '';
  };

  // Get account name for context menu filtering
  const getAccountName = (transaction) => {
    const isPlaid = isPlaidTransaction(transaction);
    let institutionName = '';
    let accountNumber = '';
    
    // For Plaid accounts
    if (isPlaid) {
      institutionName = transaction.wallet?.institution || 
                       transaction.metadata?.institutionName || 
                       'Bank Account';
      accountNumber = transaction.wallet?.lastFourDigits || 
                     transaction.metadata?.accountMask || 
                     transaction.metadata?.mask || 
                     '';
    } else {
      // For manual accounts
      institutionName = transaction.wallet?.institution || 
                       transaction.wallet?.name || 
                       'Account';
      accountNumber = transaction.wallet?.lastFourDigits || '';
    }
    
    // Clean up institution name - remove "Plaid" prefix if present
    if (institutionName && institutionName.startsWith('Plaid ')) {
      institutionName = institutionName.replace('Plaid ', '');
    }
    
    // Format the display value
    return accountNumber 
      ? `${institutionName} ••${accountNumber}` 
      : institutionName;
  };

  // Get cell value for context menu filtering
  const getCellValue = (transaction, field) => {
    switch (field) {
      case 'date':
        return transaction.date || transaction.transactionDate;
      case 'description':
        return transaction.description || transaction.name || '';
      case 'category':
        return getCategoryName(transaction);
      case 'amount':
        return transaction.amount;
      case 'account':
        return getAccountName(transaction);
      case 'source':
        return transaction.source || 'Manual';
      case 'type':
        return transaction.type || (transaction.amount >= 0 ? 'income' : 'expense');
      case 'purchaser':
        return transaction.editableOwner || transaction.ownerType || 'personal';
      case 'shared':
        return transaction.isShared ? 'Yes' : 'No';
      case 'merchant':
        return transaction.metadata?.merchantName || transaction.metadata?.merchant || '';
      case 'recurring':
        return transaction.isRecurring ? 'Yes' : 'No';
      default:
        return '';
    }
  };

  // Only apply client-side sorting if no parent sorting function is provided
  // Otherwise, assume the parent is handling server-side sorting
  const sortedTransactions = useMemo(() => {
    // Always use client-side sorting for better UX - no server calls needed
    return [...transactions].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      switch (sortField) {
        case 'date':
          return direction * (new Date(a.date) - new Date(b.date));
        case 'amount':
          return direction * (a.amount - b.amount);
        case 'description':
          return direction * a.description.localeCompare(b.description);
        case 'category':
              // Get category name from categoryRef or category using the helper function
              const categoryA = getCategoryName(a);
              const categoryB = getCategoryName(b);
          return direction * categoryA.localeCompare(categoryB);
            case 'account':
              // Sort by account name or wallet name
              const accountA = a.wallet?.name || (a.account && typeof a.account === 'object' ? a.account.name : a.account) || '';
              const accountB = b.wallet?.name || (b.account && typeof b.account === 'object' ? b.account.name : b.account) || '';
              return direction * accountA.localeCompare(accountB);
            case 'source':
              // Sort by source (Plaid or Manual)
              const isPlaidA = isPlaidTransaction(a);
              const isPlaidB = isPlaidTransaction(b);
              // First sort by source type, then by institution name for Plaid transactions
              if (isPlaidA && !isPlaidB) return direction * -1;
              if (!isPlaidA && isPlaidB) return direction * 1;
              if (isPlaidA && isPlaidB) {
                const institutionA = getInstitutionName(a) || '';
                const institutionB = getInstitutionName(b) || '';
                return direction * institutionA.localeCompare(institutionB);
              }
              return 0;
            case 'type':
              // Sort by expense/income type
              const typeA = a.type || (a.amount >= 0 ? 'income' : 'expense');
              const typeB = b.type || (b.amount >= 0 ? 'income' : 'expense');
              return direction * typeA.localeCompare(typeB);
        default:
          return 0;
      }
    });
  }, [transactions, sortField, sortDirection]);

  // Define the filteredTransactions using useMemo to avoid recomputation
  const filteredTransactions = useMemo(() => {
    // If no transactions, return empty array
    if (!sortedTransactions || sortedTransactions.length === 0) {
      if (onFilteredTransactionsChange) {
        onFilteredTransactionsChange([]);
      }
      return [];
    }
    
    const filtered = sortedTransactions.filter(transaction => {
      // Filter by type (expense/income)
      if (filters.type && filters.type !== 'all') {
        if (filters.type === 'expense' && transaction.amount > 0) return false;
        if (filters.type === 'income' && transaction.amount < 0) return false;
      }

      // Filter by search term
      if (filters.search && !transaction.description.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }

      // Filter by owner
      if (filters.owner && filters.owner !== 'all' && transaction.ownerType !== filters.owner) {
        return false;
      }

      // Filter by category - support both single category (string) and multiple categories (array)
      if (filters.category && (
        (Array.isArray(filters.category) && filters.category.length > 0) ||
        (!Array.isArray(filters.category) && filters.category !== '' && filters.category !== 'all')
      )) {
        // Normalize to array for consistent handling
        const filterCategoryIds = Array.isArray(filters.category) 
          ? filters.category.map(id => String(id).trim())
          : [String(filters.category).trim()];
        
        // Try multiple ways to get the category ID from the transaction
        // Backend sends 'category' (populated), frontend may also have 'categoryRef' for compatibility
        let transactionCategoryId = null;
        
        // Check categoryRef first (if exists)
        if (transaction.categoryRef) {
          if (typeof transaction.categoryRef === 'object' && transaction.categoryRef._id) {
            transactionCategoryId = String(transaction.categoryRef._id).trim();
          } else if (typeof transaction.categoryRef === 'string') {
            transactionCategoryId = String(transaction.categoryRef).trim();
          }
        }
        
        // Fallback to category (backend sends this)
        if (!transactionCategoryId && transaction.category) {
          if (typeof transaction.category === 'object' && transaction.category._id) {
            transactionCategoryId = String(transaction.category._id).trim();
          } else if (typeof transaction.category === 'string') {
            transactionCategoryId = String(transaction.category).trim();
          }
        }
        
        // If transaction has no category and filter is set, exclude it
        if (!transactionCategoryId) {
          return false;
        }
        
        // Check if transaction category matches any of the selected filter categories
        const matches = filterCategoryIds.some(filterId => 
          transactionCategoryId === filterId
        );
        
        if (!matches) {
          return false;
        }
      }

      // Filter by account (supports array of account IDs from filter panel)
      if (filters.accounts && Array.isArray(filters.accounts) && filters.accounts.length > 0) {
        // Get transaction's account/wallet ID
        let txAccountId = null;
        
        // Try wallet first
        if (transaction.wallet) {
          if (typeof transaction.wallet === 'object' && transaction.wallet._id) {
            txAccountId = transaction.wallet._id.toString();
          } else if (typeof transaction.wallet === 'string') {
            txAccountId = transaction.wallet;
          }
        }
        
        // Fallback to account
        if (!txAccountId && transaction.account) {
          if (typeof transaction.account === 'object' && transaction.account._id) {
            txAccountId = transaction.account._id.toString();
          } else if (typeof transaction.account === 'string') {
            txAccountId = transaction.account;
          }
        }
        
        // Check if transaction's account ID is in the selected accounts array
        if (!txAccountId || !filters.accounts.includes(txAccountId)) {
          return false;
        }
      }

      // Filter by date range
      if (filters.dateFrom && new Date(transaction.date) < new Date(filters.dateFrom)) {
        return false;
      }
      if (filters.dateTo && new Date(transaction.date) > new Date(filters.dateTo)) {
        return false;
      }

      // Filter by amount range
      if (filters.amountMin && transaction.amount < parseFloat(filters.amountMin)) {
        return false;
      }
      if (filters.amountMax && transaction.amount > parseFloat(filters.amountMax)) {
        return false;
      }

      // Filter by source (if added)
      if (filters.source) {
        const isPlaid = !!transaction.metadata?.plaidTransactionId;
        if (filters.source === 'plaid' && !isPlaid) return false;
        if (filters.source === 'manual' && isPlaid) return false;
      }

      // Filter by merchant name
      if (filters.merchant && filters.merchant.trim()) {
        const merchantName = transaction.metadata?.merchantName || 
                            transaction.merchant || 
                            transaction.description || '';
        if (!merchantName.toLowerCase().includes(filters.merchant.toLowerCase())) {
          return false;
        }
      }

      // Filter by shared status - matches SHARED column display logic
      if (filters.shared && filters.shared !== 'all') {
        const ownerType = transaction.ownerType || 'personal';
        const wallet = transaction.wallet || transaction.account;
        const isJointAccount = wallet?.isJoint || wallet?.isJointAccount || transaction.isJointAccount || false;
        const isShared = transaction.isShared || transaction.isSharedTransaction;
        
        // Check if this is a partner transaction (has originalOwner or ownerType is partner)
        const hasPartnerOwner = transaction.originalOwner || ownerType === 'partner';
        
        if (filters.shared === 'personal') {
          // Personal: ownerType is personal AND not shared AND not joint
          if (ownerType !== 'personal' || isShared || isJointAccount) return false;
        } else if (filters.shared === 'partner') {
          // Partner: ownerType is partner OR (isShared with originalOwner) - NOT joint
          if (isJointAccount || ownerType === 'joint') return false;
          if (ownerType !== 'partner' && !isShared) return false;
        } else if (filters.shared === 'joint') {
          // Joint: ownerType is joint OR account is joint
          if (ownerType !== 'joint' && !isJointAccount) return false;
        }
      }

      // Filter by recurring status
      if (filters.recurring && filters.recurring !== 'all') {
        const isRecurring = transaction.isRecurring || false;
        if (filters.recurring === 'recurring' && !isRecurring) return false;
        if (filters.recurring === 'one-time' && isRecurring) return false;
      }

      // Filter by notes
      if (filters.notes && filters.notes.trim()) {
        const notes = transaction.notes || '';
        if (!notes.toLowerCase().includes(filters.notes.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
    
    // Notify parent of filtered transactions count
    if (onFilteredTransactionsChange) {
      onFilteredTransactionsChange(filtered);
    }
    
    return filtered;
  }, [sortedTransactions, filters, onFilteredTransactionsChange]);

  // Replace the handleSelectAll function with a memoized version
  const notifyParentOfSelectionChange = useCallback((ids) => {
    if (onSelectionChange) {
      onSelectionChange(ids);
    }
  }, [onSelectionChange]);

  // Determine if we should use server-side or client-side pagination
  // If onPageChange is provided, use server-side pagination (show all filtered transactions)
  // Otherwise, use client-side pagination
  const useServerPagination = !!onPageChange;
  
  // When using server-side pagination, we should show all transactions from current page
  // When using client-side pagination, paginate the filtered transactions
  const paginatedTransactions = useMemo(() => {
    if (useServerPagination) {
      // Server-side pagination: show all transactions (they're already paginated by server)
      // Apply filters but don't paginate further
      return filteredTransactions;
    } else {
      // Client-side pagination: paginate the filtered transactions
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return filteredTransactions.slice(startIndex, endIndex);
    }
  }, [filteredTransactions, currentPage, itemsPerPage, useServerPagination]);
  
  const totalPages = useServerPagination 
    ? 1 // Server handles pagination, we just show current page
    : Math.ceil(filteredTransactions.length / itemsPerPage);
  
  const handleSelectAll = useCallback((e) => {
    const checked = e.target.checked;
    
    // Only select transactions on the current page
    const newSelectedIds = checked 
      ? paginatedTransactions.map(tx => tx._id)
      : [];
    
    // Update local state
    setSelectAll(checked);
    setSelectedTransactionIds(newSelectedIds);
    
    // Notify parent
    notifyParentOfSelectionChange(newSelectedIds);
  }, [paginatedTransactions, notifyParentOfSelectionChange]);
  
  // Update handleSelectTransaction to use the local state only
  const handleSelectTransaction = useCallback((transactionId, checked) => {
    // Create a new array based on the current state
    const newSelectedIds = checked
      ? [...selectedTransactionIds, transactionId]
      : selectedTransactionIds.filter(id => id !== transactionId);
    
    // Update local state
    setSelectedTransactionIds(newSelectedIds);
    
    // Update selectAll based on whether all transactions on current page are selected
    const pageTransactionIds = paginatedTransactions.map(tx => tx._id);
    setSelectAll(
      pageTransactionIds.length > 0 && 
      pageTransactionIds.every(id => newSelectedIds.includes(id))
    );
    
    // Notify parent
    notifyParentOfSelectionChange(newSelectedIds);
  }, [selectedTransactionIds, paginatedTransactions, notifyParentOfSelectionChange]);
  
  // Handle batch delete
  const handleBatchDelete = async () => {
    if (selectedTransactionIds.length === 0) {
      toast.error('No transactions selected');
      return;
    }
    
    // Confirmation message
    const message = selectedTransactionIds.length === 1
      ? 'Are you sure you want to delete this transaction?'
      : `Are you sure you want to delete ${selectedTransactionIds.length} transactions?`;
      
    if (window.confirm(message)) {
      setLoading(true);
      try {
        const response = await transactionService.bulkDeleteTransactions(selectedTransactionIds);
        
        if (response.success) {
          const deletedCount = response.deletedCount || 0;
          const skippedCount = response.plaidTransactionsSkipped || 0;
          
          let successMessage = `Deleted ${deletedCount} transactions successfully`;
          if (skippedCount > 0) {
            successMessage += `. ${skippedCount} Plaid transactions were skipped (cannot be deleted).`;
          }
          
          toast.success(successMessage);
          
          // Clear selection
          setSelectedTransactionIds([]);
          setSelectAll(false);
          
          // Refresh transactions
          onTransactionUpdate && onTransactionUpdate();
        } else {
          toast.error('Failed to delete transactions: ' + (response.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error deleting transactions:', error);
        toast.error('Failed to delete transactions: ' + (error.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    }
  };

  // Define all available columns (including new fields) - memoized to prevent recreation
  const allAvailableColumns = useMemo(() => [
    { field: 'date', label: 'Date' },
    { field: 'description', label: 'Description' },
    { field: 'category', label: 'Category' },
    { field: 'amount', label: 'Amount' },
    { field: 'account', label: 'Account' },
    { field: 'source', label: 'Source' },
    { field: 'type', label: 'Type' },
    { field: 'purchaser', label: 'Owner' },
    { field: 'merchant', label: 'Merchant' },
    { field: 'recurring', label: 'Recurring' },
    { field: 'notes', label: 'Notes' },
    ...(hasPartnership ? [{ field: 'shared', label: 'Shared' }] : [])
  ], [hasPartnership]);

  // Load column preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setLoadingPreferences(true);
        console.log('🔄 Loading column preferences...');
        const preferences = await userPreferencesService.getTransactionColumnPreferences();
        console.log('✅ Loaded column preferences:', preferences);
        setColumnPreferences(preferences);
      } catch (error) {
        console.error('❌ Error loading column preferences:', error);
        // Continue with defaults if loading fails
        setColumnPreferences(null);
      } finally {
        setLoadingPreferences(false);
      }
    };
    loadPreferences();
  }, []);

  // Expose column modal handler to parent
  useEffect(() => {
    if (onColumnCustomizeClick) {
      onColumnCustomizeClick.current = () => setIsColumnModalOpen(true);
    }
  }, [onColumnCustomizeClick]);

  // Apply column preferences to determine visible columns and order
  const columns = useMemo(() => {
    // Wait for preferences to load (if still loading, return defaults)
    if (loadingPreferences) {
      return allAvailableColumns;
    }
    
    if (!columnPreferences) {
      // Default: show all columns in default order
      console.log('📊 No saved preferences, using default columns');
      return allAvailableColumns;
    }

    console.log('📊 Applying saved preferences:', columnPreferences);
    const { columnOrder, visibleColumns } = columnPreferences;
    
    // If we have a saved order, use it
    if (columnOrder && columnOrder.length > 0) {
      // Reorder based on saved order
      const orderedColumns = columnOrder
        .map(field => allAvailableColumns.find(col => col.field === field))
        .filter(Boolean);
      
      // Add any new columns that weren't in saved preferences
      const existingFields = new Set(columnOrder);
      const newColumns = allAvailableColumns.filter(col => !existingFields.has(col.field));
      
      const allColumns = [...orderedColumns, ...newColumns];
      
      // Filter by visibility if specified
      if (visibleColumns && visibleColumns.length > 0) {
        const visibleSet = new Set(visibleColumns);
        return allColumns.filter(col => visibleSet.has(col.field));
      }
      
      return allColumns;
    }
    
    // If we only have visibility preferences, filter existing order
    if (visibleColumns && visibleColumns.length > 0) {
      const visibleSet = new Set(visibleColumns);
      return allAvailableColumns.filter(col => visibleSet.has(col.field));
    }
    
    // Fallback to all columns
    return allAvailableColumns;
  }, [columnPreferences, allAvailableColumns, hasPartnership, loadingPreferences]);

  const handleUpdateDescription = async (transactionId, newDescription) => {
    try {
      await transactionService.updateTransaction(transactionId, { description: newDescription });
      toast.success('Description updated successfully');
      onTransactionUpdate && onTransactionUpdate();
    } catch (error) {
      toast.error('Failed to update description');
      console.error(error);
    }
  };

  const handleUpdateCategory = async (transactionId, categoryId) => {
    setLoading(true);
    try {
      // Check if this is a Plaid transaction (ID starts with "plaid_")
      const isPlaidTransaction = typeof transactionId === 'string' && transactionId.startsWith('plaid_');
      
      if (isPlaidTransaction) {
        // For Plaid transactions, just update the category in the UI
        console.log('🔄 Updating Plaid transaction category in UI only:', {
          transactionId,
          categoryId
        });
        
        // Find the selected category object
        const category = categories.find(c => c._id === categoryId);
        
        if (!category) {
          toast.error('Selected category not found');
          return;
        }
        
        // Update the transaction in the local state only
        const updatedTransactions = transactions.map(t => {
          if (t._id === transactionId) {
            // Create a new transaction object with updated category
            return { 
              ...t, 
              category: {
                _id: category._id,
                name: category.name,
                color: category.color
              }
            };
          }
          return t;
        });
        
        // Use the callback to update the transactions in the parent component
        onTransactionUpdate && onTransactionUpdate(updatedTransactions);
        toast.success('Category updated for Plaid transaction (UI only)');
      } else {
        // For standard transactions, update in the database
        await transactionService.updateTransaction(transactionId, { 
          category: categoryId 
        });
        toast.success('Category updated successfully');
      
        // Update the transaction in the local state
        const updatedTransactions = transactions.map(t => {
          if (t._id === transactionId) {
            const category = categories.find(c => c._id === categoryId);
            return { ...t, category: category };
          }
          return t;
        });
      
        onTransactionUpdate && onTransactionUpdate(updatedTransactions);
      }
      
      setEditingCategoryId(null);
    } catch (error) {
      toast.error('Failed to update category');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle auto-categorization
  const handleBatchAutoCategorize = async () => {
    try {
      setAutoCategorizing(true);
      
      const response = await smartCategorizationService.batchCategorize({
        onlyUncategorized: true,
        minimumConfidence: 0.4,
        includePlaidTransactions: true
      });
      
      if (response.success) {
        const { updated, skipped, processed } = response.data;
        
        toast.success(
          `Auto-categorized ${updated} of ${processed} transactions`,
          { duration: 5000 }
        );
        
        if (onTransactionUpdate) {
          setTimeout(() => {
            onTransactionUpdate();
          }, 500);
        }
      } else {
        toast.error('Failed to auto-categorize transactions');
      }
    } catch (error) {
      console.error('Error auto-categorizing transactions:', error);
      toast.error('An error occurred during auto-categorization');
    } finally {
      setAutoCategorizing(false);
    }
  };

  // Handle auto-categorization for a single transaction
  const handleAutoCategorizeSingle = async (transactionId) => {
    try {
      setLoading(true);
      toast.loading('Auto-categorizing transaction...', { id: 'auto-cat-single' });
      console.log('🤖 Auto-categorizing single transaction:', transactionId);
      
      const response = await smartCategorizationService.categorizeTransaction(transactionId);
      console.log('🤖 Auto-categorization response:', response);
      
      if (response.success) {
        toast.success('Transaction auto-categorized successfully', { id: 'auto-cat-single' });
        
        // Use a callback for safer state updates
        if (onTransactionUpdate) {
          // Wait a moment before triggering the update
          setTimeout(() => {
            onTransactionUpdate();
          }, 300);
        }
      } else {
        console.error('❌ Failed to auto-categorize transaction:', response);
        toast.error(`Failed to auto-categorize: ${response.message || 'Unknown error'}`, { id: 'auto-cat-single' });
      }
    } catch (error) {
      console.error('❌ Error auto-categorizing transaction:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Auto-categorization failed';
      toast.error(errorMessage, { id: 'auto-cat-single' });
    } finally {
      setLoading(false);
    }
  };

  const renderConfidenceBadge = () => {
    // No longer needed as we're displaying confidence in the CategoryTag
    return null;
  };

  const renderCell = (transaction, field) => {
    // Removed excessive console.log that was causing 1000+ logs
    
    const isPlaid = isPlaidTransaction(transaction);
    const isPending = isPlaid && transaction.pending;
    
    switch (field) {
      case 'date':
        return <span className="text-gray-800 text-[10px] md:text-xs">{new Date(transaction.date).toLocaleDateString()}</span>;
      case 'description':
        const description = transaction.description || '';
        const isExpanded = expandedDescriptionId === transaction._id;
        const isLongDescription = description.length > 50;
        
        return (
          <div className="flex items-center group relative w-full overflow-hidden">
            <div 
              className="flex-1 min-w-0 overflow-hidden"
              onClick={() => {
                if (isLongDescription) {
                  setExpandedDescriptionId(isExpanded ? null : transaction._id);
                }
              }}
            >
              {isExpanded ? (
                <span className="text-gray-800 text-[10px] md:text-xs break-words cursor-pointer block">
                  {description}
                </span>
              ) : (
                <span 
                  className="text-gray-800 text-[10px] md:text-xs truncate cursor-pointer hover:text-indigo-600 transition-colors block overflow-hidden text-ellipsis whitespace-nowrap" 
                  title={isLongDescription ? `Click to expand: ${description}` : description}
                  style={{ maxWidth: '100%' }}
                >
                  {description}
                </span>
              )}
            </div>
            {/* Hover tooltip for desktop */}
            {isLongDescription && !isExpanded && (
              <div className="hidden md:block absolute left-0 top-full mt-1 z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none break-words">
                {description}
              </div>
            )}
            {transaction.isSharedTransaction && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-blue-100 text-blue-800" title={`Shared by ${transaction.ownerName || 'Partner'}`}>
                <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                </svg>
                Shared
              </span>
            )}
            {isPending && (
              <span className="ml-1" title="Pending Transaction">
                <ClockIcon className="h-3 w-3 text-yellow-500" />
              </span>
            )}
          </div>
        );
      case 'amount': {
        // No need to invert the sign, as our internal convention already has:
        // - Positive amounts for income
        // - Negative amounts for expenses
        const isPositive = transaction.amount >= 0;
        return (
          <span className={`${isPositive ? 'text-green-600' : 'text-red-600'} text-[10px] md:text-xs ${isPending ? 'italic' : ''}`}>
            {isPositive ? '+' : ''}{formatCurrency(transaction.amount)}
          </span>
        );
      }
      case 'source':
        if (isPlaid) {
          return (
            <div className="flex items-center" title={`Imported from ${getInstitutionName(transaction)}`}>
              <BanknotesIcon className="h-3 w-3 text-blue-500 mr-1" />
              <span className="text-[10px] md:text-xs text-blue-600">Plaid</span>
            </div>
          );
        } else if (transaction.source === 'csv') {
          return <span className="text-[10px] md:text-xs text-gray-500">CSV Import</span>;
        } else if (transaction.source) {
          return <span className="text-[10px] md:text-xs text-gray-500">{transaction.source}</span>;
        } else {
          return <span className="text-[10px] md:text-xs text-gray-500">Manual</span>;
        }
      case 'category':
        if (editingCategoryId === transaction._id) {
          return (
            <div className="relative z-50">
              <CategorySelector
                categories={categories}
                currentCategoryId={transaction.categoryRef?._id}
                onSelect={(categoryId) => handleUpdateCategory(transaction._id, categoryId)}
                onCancel={() => setEditingCategoryId(null)}
                isLoading={loading}
              />
            </div>
          );
        }
        
        return (
          <div className="flex items-center">
            {/* Category tag shows category name and color - with error handling */}
            {(() => {
              try {
                return (
                  <CategoryTag
                    category={transaction.categoryRef || transaction.category || null}
                    confidence={transaction.confidence}
                    onClick={() => setEditingCategoryId(transaction._id)}
                  />
                );
              } catch (error) {
                console.error('Error rendering CategoryTag:', error);
                return (
                  <span 
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-gray-200 text-gray-800 cursor-pointer"
                    onClick={() => setEditingCategoryId(transaction._id)}
                  >
                    Uncategorized
                  </span>
                );
              }
            })()}
            
            {/* Show auto-categorize button for uncategorized transactions, including Plaid ones */}
            {(!transaction.categoryRef && !transaction.category) && (
              <button
                onClick={() => handleAutoCategorizeSingle(transaction._id)}
                disabled={loading}
                className="ml-1 flex items-center px-1.5 py-0.5 bg-indigo-500 text-white rounded text-xs hover:bg-indigo-600 focus:outline-none disabled:opacity-50"
                title="Auto-categorize this transaction"
              >
                <SparklesIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        );
      case 'account':
        // Get institution name from different possible sources
        let institutionName = '';
        let accountNumber = '';
        
        // For Plaid accounts
        if (isPlaid) {
          // Try to get the institution name from populated wallet data first
          institutionName = transaction.wallet?.institution || 
                           transaction.metadata?.institutionName || 
                           'Bank Account';
          
          // Try to get the account mask/last4
          accountNumber = transaction.wallet?.lastFourDigits || 
                         transaction.metadata?.accountMask || 
                         transaction.metadata?.mask || 
                         '';
        } else {
          // For manual accounts
          institutionName = transaction.wallet?.institution || 
                           transaction.wallet?.name || 
                           'Account';
          accountNumber = transaction.wallet?.lastFourDigits || '';
        }
        
        // Clean up institution name - remove "Plaid" prefix if present
        if (institutionName && institutionName.startsWith('Plaid ')) {
          institutionName = institutionName.replace('Plaid ', '');
        }
        
        // Format the display value
        const displayValue = accountNumber 
          ? `${institutionName} ••${accountNumber}` 
          : institutionName;
                           
        return (
          <div className="flex items-center">
            <span className="text-gray-800 text-xs">{displayValue}</span>
          </div>
        );
      case 'type':
        // Use transaction's explicit type if available
        if (transaction.type) {
          switch (transaction.type) {
            case 'income':
              return (
                <div className="flex items-center">
                  <span 
                    className="text-green-600 text-xs font-medium px-2.5 py-1 bg-green-100 rounded-full cursor-pointer hover:bg-green-200 transition-colors"
                    onClick={() => handleUpdateTransactionType(transaction._id)}
                    title="Change transaction type"
                  >
                    <span className="flex items-center">
                      <ArrowDownCircleIcon className="h-3 w-3 mr-1" />
                      Income
                    </span>
                  </span>
                </div>
              );
            case 'expense':
              return (
                <div className="flex items-center">
                  <span 
                    className="text-red-600 text-xs font-medium px-2.5 py-1 bg-red-100 rounded-full cursor-pointer hover:bg-red-200 transition-colors"
                    onClick={() => handleUpdateTransactionType(transaction._id)}
                    title="Change transaction type"
                  >
                    <span className="flex items-center">
                      <ArrowUpCircleIcon className="h-3 w-3 mr-1" />
                      Expense
                    </span>
                  </span>
                </div>
              );
            case 'bills':
              return (
                <div className="flex items-center">
                  <span 
                    className="text-amber-600 text-xs font-medium px-2.5 py-1 bg-amber-100 rounded-full cursor-pointer hover:bg-amber-200 transition-colors"
                    onClick={() => handleUpdateTransactionType(transaction._id)}
                    title="Change transaction type"
                  >
                    <span className="flex items-center">
                      <BanknotesIcon className="h-3 w-3 mr-1" />
                      Bills
                    </span>
                  </span>
                </div>
              );
            case 'payment':
              return (
                <div className="flex items-center">
                  <span 
                    className="text-purple-600 text-xs font-medium px-2.5 py-1 bg-purple-100 rounded-full cursor-pointer hover:bg-purple-200 transition-colors"
                    onClick={() => handleUpdateTransactionType(transaction._id)}
                    title="Change transaction type"
                  >
                    <span className="flex items-center">
                      <CreditCardIcon className="h-3 w-3 mr-1" />
                      Payment
                    </span>
                  </span>
                </div>
              );
            case 'transfer':
              return (
                <div className="flex items-center">
                  <span 
                    className="text-blue-600 text-xs font-medium px-2.5 py-1 bg-blue-100 rounded-full cursor-pointer hover:bg-blue-200 transition-colors"
                    onClick={() => handleUpdateTransactionType(transaction._id)}
                    title="Change transaction type"
                  >
                    <span className="flex items-center">
                      <ArrowPathIcon className="h-3 w-3 mr-1" />
                      Transfer
                    </span>
                  </span>
                </div>
              );
            default:
              return (
                <div className="flex items-center">
                  <span 
                    className="text-gray-600 text-xs font-medium px-2.5 py-1 bg-gray-100 rounded-full cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handleUpdateTransactionType(transaction._id)}
                    title="Change transaction type"
                  >
                    <span className="flex items-center">
                      <TagIcon className="h-3 w-3 mr-1" />
                      {transaction.type}
                    </span>
                  </span>
                </div>
              );
          }
        }
        
        // If type not explicitly set, determine default based on amount and description
        const amount = typeof transaction.amount === 'number' ? transaction.amount : 0;
        const transactionDescription = (transaction.description || '').toLowerCase();
        
        // Check if it's a payment (e.g., credit card payment)
        const paymentKeywords = ['payment', 'autopay', 'auto pay', 'thank you'];
        const isPayment = paymentKeywords.some(keyword => transactionDescription.includes(keyword));
        
        if (isPayment) {
          return (
            <div className="flex items-center">
              <span 
                className="text-purple-600 text-xs font-medium px-2.5 py-1 bg-purple-100 rounded-full cursor-pointer hover:bg-purple-200 transition-colors"
                onClick={() => handleUpdateTransactionType(transaction._id)}
                title="Change transaction type"
              >
                <span className="flex items-center">
                  <CreditCardIcon className="h-3 w-3 mr-1" />
                  Payment
                </span>
              </span>
            </div>
          );
        }
        
        // Default based on amount sign
        const defaultType = amount >= 0 ? 'income' : 'expense';
        
        return (
          <div className="flex items-center">
            <span 
              className={`${defaultType === 'income' 
                ? 'text-green-600 bg-green-100 hover:bg-green-200' 
                : 'text-red-600 bg-red-100 hover:bg-red-200'} 
                text-xs font-medium px-2.5 py-1 rounded-full cursor-pointer transition-colors`}
              onClick={() => handleUpdateTransactionType(transaction._id)}
              title="Change transaction type"
            >
              <span className="flex items-center">
                {defaultType === 'income' ? <ArrowDownCircleIcon className="h-3 w-3 mr-1" /> : <ArrowUpCircleIcon className="h-3 w-3 mr-1" />}
                {defaultType === 'income' ? 'Income' : 'Expense'}
              </span>
            </span>
          </div>
        );
      case 'purchaser': {
        // OWNER COLUMN - Uses editableOwner field (defaults to ownerType if not set)
        // This is separate from SHARED column and can be edited independently
        const editableOwner = transaction.editableOwner || transaction.ownerType || 'personal';
        
        // Get owner name from various sources
        let ownerName = transaction.ownerName || 
                         transaction.originalOwner?.firstName ||
                         transaction.user?.firstName ||
                         null;
        
        // If editableOwner is partner but no name found, try to get partner name from user context
        if (editableOwner === 'partner' && !ownerName) {
          ownerName = user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner';
        }
        
        let displayContent;
        
        // Show based on editableOwner (user's choice)
        if (editableOwner === 'joint') {
          displayContent = (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Joint
            </span>
          );
        } else if (editableOwner === 'partner') {
          // Partner assigned
          displayContent = (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <UserIcon className="h-3 w-3 mr-1" />
              {ownerName || 'Partner'}
            </span>
          );
        } else {
          // Personal (current user)
          displayContent = (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              <UserIcon className="h-3 w-3 mr-1" />
              {user?.firstName || 'You'}
            </span>
          );
        }
        
        return (
          <div className="relative">
            <div
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleUpdateTransactionOwner(transaction._id)}
              title="Click to change owner"
            >
              {displayContent}
            </div>
          </div>
        );
      }
      case 'merchant': {
        // Show merchant name from metadata or description parsing
        const merchantName = transaction.metadata?.merchantName ||
                           transaction.metadata?.merchant ||
                           transaction.metadata?.name ||
                           (transaction.description ? transaction.description.split(' ').slice(0, 2).join(' ') : '');
        
        return (
          <div className="flex items-center">
            <BuildingStorefrontIcon className="h-3 w-3 text-gray-400 mr-1 flex-shrink-0" />
            <span className="text-xs text-gray-700 truncate" title={merchantName}>
              {merchantName}
            </span>
          </div>
        );
      }
      case 'recurring': {
        // Show recurring indicator
        if (transaction.isRecurring || transaction.recurringGroupId) {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" title="Recurring transaction">
              <ArrowPathRoundedSquareIcon className="h-3 w-3 mr-1" />
              Recurring
            </span>
          );
        }
        return <span className="text-xs text-gray-400">—</span>;
      }
      case 'notes': {
        // Show notes if available
        const notes = transaction.notes || transaction.metadata?.notes || '';
        if (notes) {
          return (
            <span className="text-xs text-gray-700 truncate" title={notes}>
              {notes}
            </span>
          );
        }
        return <span className="text-xs text-gray-400">—</span>;
      }
      case 'shared': {
        // 🏦 PRIORITY 1: Check ownerType field (set by our database script)
        const ownerType = transaction.ownerType || 'personal';
        
        // 🏦 PRIORITY 2: Check if account is joint
        const wallet = transaction.wallet || transaction.account;
        const isJointAccount = wallet?.isJoint || wallet?.isJointAccount || transaction.isJointAccount || false;
        const isShared = transaction.isShared || transaction.isSharedTransaction;
        
        // Get shared with name
        let sharedWithName = null;
        if (transaction.ownerName) {
          sharedWithName = transaction.ownerName;
        } else if (transaction.originalOwner) {
          if (typeof transaction.originalOwner === 'string') {
            sharedWithName = null;
          } else {
            sharedWithName = transaction.originalOwner.firstName || transaction.originalOwner.name || null;
          }
        }
        
        // Try to get partner name if needed
        if (ownerType === 'partner' && !sharedWithName && user) {
          sharedWithName = user.partner?.firstName || user.createdPartner?.firstName || 'Partner';
        }
        
        // Display based on ownerType
        if (ownerType === 'joint' || isJointAccount) {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" title="Joint Account">
              Joint
            </span>
          );
        } else if (ownerType === 'partner' && sharedWithName) {
          // Partner's transaction shared with you
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" title={`Shared from ${sharedWithName}`}>
              {sharedWithName}
            </span>
          );
        } else if (ownerType === 'personal' && isShared && sharedWithName) {
          // Your transaction shared with partner - show partner's name
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" title={`Shared with ${sharedWithName}`}>
              {sharedWithName}
            </span>
          );
        } else {
          // Personal transaction, not shared
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600" title="Personal Account">
              Personal
            </span>
          );
        }
      }
      default:
        return transaction[field] || '';
    }
  };

  const handleUpdateTransactionType = (transactionId) => {
    setEditingTypeId(transactionId === editingTypeId ? null : transactionId);
  };

  const handleUpdateTransactionOwner = (transactionId) => {
    setEditingOwnerId(transactionId === editingOwnerId ? null : transactionId);
  };

  const updateTransactionOwner = async (transactionId, ownerType, ownerDetails = null) => {
    try {
      // Update editableOwner field (not ownerType - that's for SHARED column)
      await transactionService.updateTransaction(transactionId, {
        editableOwner: ownerType,
        ...(ownerDetails ? { ownerName: ownerDetails.name } : {})
      });
      toast.success('Owner updated successfully');
      setEditingOwnerId(null);
      onTransactionUpdate && onTransactionUpdate();
    } catch (error) {
      toast.error('Failed to update owner');
      console.error(error);
    }
  };

  const updateTransactionType = async (transactionId, newType) => {
    setLoading(true);
    try {
      // Check if this is a Plaid transaction
      const isPlaidTransaction = typeof transactionId === 'string' && transactionId.startsWith('plaid_');
      
      if (isPlaidTransaction) {
        // For Plaid transactions, just update in the UI
        console.log('Updating Plaid transaction type in UI only:', {
          transactionId,
          newType
        });
        
        // Update the transaction in the local state only
        const updatedTransactions = transactions.map(t => {
          if (t._id === transactionId) {
            return { ...t, type: newType };
          }
          return t;
        });
        
        // Update parent component
        onTransactionUpdate && onTransactionUpdate(updatedTransactions);
        toast.success('Transaction type updated (UI only)');
      } else {
        // For standard transactions, update in the database
        const response = await transactionService.updateTransaction(transactionId, { 
          type: newType 
        });
        
        if (response && response.success) {
          console.log('Server response after type update:', response);
          
          // Create an updated version of transactions for immediate UI update
          const updatedTransactions = transactions.map(t => {
            if (t._id === transactionId) {
              // Preserve all existing data and override with server response
              return { ...t, type: newType, ...response.data };
            }
            return t;
          });
          
          // Update parent component with the refreshed data
          if (onTransactionUpdate) {
            onTransactionUpdate(updatedTransactions);
          }
          
          toast.success('Transaction type updated successfully');
        } else {
          throw new Error('Server returned unsuccessful response');
        }
      }
      
      // Close the type selector
      setEditingTypeId(null);
    } catch (error) {
      toast.error('Failed to update transaction type');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (transaction) => {
    setSelectedTransaction(transaction);
    setEditModalOpen(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await transactionService.deleteTransaction(id);
        toast.success('Transaction deleted successfully');
        onTransactionUpdate && onTransactionUpdate();
      } catch (error) {
        toast.error('Failed to delete transaction');
        console.error(error);
      }
    }
  };

  // Add back the category fetching code
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryService.getAllCategories();
        if (response.success) {
          setCategories(response.data);
        } else {
          console.error('Failed to fetch categories:', response.message);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);

  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No transactions found
      </div>
    );
  }

  // Count uncategorized transactions
  const uncategorizedCount = transactions.filter(
    t => !t.categoryRef && !t.category
  ).length;

  return (
    <ErrorBoundary>
      <div className="overflow-hidden">
        {/* Action Bar */}
        <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center space-x-4">
            {/* Refresh button */}
            <button
              onClick={() => {
                // Show immediate feedback
                toast.loading('Refreshing transactions...', { id: 'refresh-transactions' });
                
                // Call the parent's refresh function
                if (onTransactionUpdate) {
                  onTransactionUpdate();
                  // Show success message after a short delay
                  setTimeout(() => {
                    toast.success('Transactions refreshed successfully', { id: 'refresh-transactions' });
                  }, 500);
                } else {
                  toast.error('Refresh function not available', { id: 'refresh-transactions' });
                }
              }}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              title="Refresh transactions"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2 text-gray-500" />
              Refresh
            </button>
            
            {/* Filter button */}
            <button
              onClick={onFilterClick}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FunnelIcon className="h-5 w-5 mr-2 text-gray-500" />
              Filter
            </button>

            {/* Bulk Edit button - only show when transactions are selected */}
            {selectedTransactionIds.length > 0 && (
              <button
                onClick={() => setIsBulkEditModalOpen(true)}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-indigo-700 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                Edit {selectedTransactionIds.length > 1 ? `(${selectedTransactionIds.length})` : ''}
              </button>
            )}

            {/* Delete button - only show when transactions are selected */}
            {selectedTransactionIds.length > 0 && (
              <button
                onClick={handleBatchDelete}
                disabled={loading}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-700 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                Delete {selectedTransactionIds.length > 1 ? `(${selectedTransactionIds.length})` : ''}
              </button>
            )}
          </div>
          
          {/* Selected transaction count */}
          {selectedTransactionIds.length > 0 && (
            <div className="text-sm text-gray-600">
              {selectedTransactionIds.length} transaction{selectedTransactionIds.length !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>

        <div className="relative overflow-x-auto bg-white rounded-lg shadow" style={{ maxWidth: '100%' }}>
          {/* Mobile scroll indicator */}
          <div className="md:hidden absolute top-2 right-2 z-10 bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l4-4m0 0l4 4m-4-4v12" />
            </svg>
            Swipe to scroll
          </div>
          <table className="w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input 
                    type="checkbox" 
                    checked={selectAll && filteredTransactions.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50" 
                  />
                </th>
                {columns.map(column => {
                  // Define column widths based on content type
                  const getColumnWidth = (field) => {
                    // For table-fixed layout, use percentage or fixed pixel widths
                    const widthMap = {
                      'date': { className: 'w-16 md:w-20', style: { width: '80px', minWidth: '80px' } },
                      'description': { className: 'w-56 md:w-96 lg:w-[28rem]', style: { width: '300px', minWidth: '300px' } }, // Reduced by 25% (was 400px)
                      'category': { className: 'w-24 md:w-32', style: { width: '120px', minWidth: '120px' } },
                      'amount': { className: 'w-20 md:w-28', style: { width: '100px', minWidth: '100px' } },
                      'account': { className: 'w-32 md:w-40', style: { width: '150px', minWidth: '150px' } },
                      'source': { className: 'w-16 md:w-20', style: { width: '70px', minWidth: '70px' } },
                      'type': { className: 'w-24 md:w-28', style: { width: '100px', minWidth: '100px' } },
                      'purchaser': { className: 'w-20 md:w-24', style: { width: '75px', minWidth: '75px' } }, // Reduced (was 90px)
                      'shared': { className: 'w-16 md:w-20', style: { width: '70px', minWidth: '70px' } },
                      'merchant': { className: 'w-32 md:w-40', style: { width: '150px', minWidth: '150px' } },
                      'recurring': { className: 'w-16 md:w-20', style: { width: '70px', minWidth: '70px' } },
                      'notes': { className: 'w-36 md:w-48', style: { width: '180px', minWidth: '180px' } }
                    };
                    return widthMap[field] || { className: 'w-auto', style: {} };
                  };
                  
                  const widthConfig = getColumnWidth(column.field);
                  return (
                  <th 
                    key={column.field}
                    className={`${widthConfig.className} px-2 md:px-3 py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    style={widthConfig.style}
                    onClick={(e) => {
                      // Prevent any browser default behaviors
                      e.preventDefault();
                      e.stopPropagation();
                      
                      // Don't sort if we're editing a category
                      if (editingCategoryId) {
                        return;
                      }
                      
                      // Determine new sort direction
                      const newDirection = sortField === column.field 
                        ? (sortDirection === 'asc' ? 'desc' : 'asc') 
                        : 'desc';
                      
                      // Update local state for UI - client-side sorting only
                      setSortField(column.field);
                      setSortDirection(newDirection);
                      
                      // No server-side sorting calls - better UX with instant sorting
                      console.log(`🔄 Sorting by ${column.field} (${newDirection}) - client-side only`);
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      setContextMenu({
                        isOpen: true,
                        position: { x: e.clientX, y: e.clientY },
                        type: 'header',
                        data: { column }
                      });
                    }}
                    className={`cursor-pointer hover:bg-gray-100 select-none ${
                      sortField === column.field 
                        ? 'text-indigo-600 bg-indigo-50'
                        : 'text-gray-500'
                    }`}
                    title={`Sort by ${column.label} (Right-click for more options)`}
                  >
                    <div className="flex items-center space-x-1">
                      <span className="truncate">{column.label}</span>
                    {sortField === column.field && (
                        <span className="text-indigo-600 flex-shrink-0">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                    )}
                    </div>
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedTransactions.map(transaction => {
                const isPlaid = isPlaidTransaction(transaction);
                const isPending = isPlaid && transaction.pending;
                const isSelected = selectedTransactionIds.includes(transaction._id);
                
                return (
                  <tr 
                    key={transaction._id} 
                    className={`hover:bg-gray-50 ${isPending ? 'bg-yellow-50' : ''} ${isPlaid ? 'bg-blue-50/30' : ''} ${isSelected ? 'bg-indigo-50' : ''}`}
                  >
                  <td className="w-12 px-2 py-2 text-gray-700">
                    <input 
                      type="checkbox" 
                      checked={isSelected}
                      onChange={(e) => handleSelectTransaction(transaction._id, e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50" 
                    />
                  </td>
                  {columns.map(column => {
                    // Match column widths with header
                    const getColumnWidth = (field) => {
                      // For table-fixed layout, use percentage or fixed pixel widths
                      const widthMap = {
                        'date': { className: 'w-16 md:w-20', style: { width: '80px', minWidth: '80px' } },
                        'description': { className: 'w-56 md:w-96 lg:w-[28rem]', style: { width: '300px', minWidth: '300px' } }, // Reduced by 25% (was 400px)
                        'category': { className: 'w-24 md:w-32', style: { width: '120px', minWidth: '120px' } },
                        'amount': { className: 'w-20 md:w-28', style: { width: '100px', minWidth: '100px' } },
                        'account': { className: 'w-32 md:w-40', style: { width: '150px', minWidth: '150px' } },
                        'source': { className: 'w-16 md:w-20', style: { width: '70px', minWidth: '70px' } },
                        'type': { className: 'w-24 md:w-28', style: { width: '100px', minWidth: '100px' } },
                        'purchaser': { className: 'w-20 md:w-24', style: { width: '75px', minWidth: '75px' } }, // Reduced (was 90px)
                        'shared': { className: 'w-16 md:w-20', style: { width: '70px', minWidth: '70px' } },
                        'merchant': { className: 'w-32 md:w-40', style: { width: '150px', minWidth: '150px' } },
                        'recurring': { className: 'w-16 md:w-20', style: { width: '70px', minWidth: '70px' } },
                        'notes': { className: 'w-36 md:w-48', style: { width: '180px', minWidth: '180px' } }
                      };
                      return widthMap[field] || { className: 'w-auto', style: {} };
                    };
                    
                    return (
                    <td 
                      key={column.field} 
                      className={`${getColumnWidth(column.field)} px-2 md:px-3 py-2 text-[10px] md:text-xs text-gray-700 ${column.field === 'type' || column.field === 'purchaser' ? 'relative' : ''} ${
                        column.field === 'description' || column.field === 'merchant' || column.field === 'notes' ? '' : 'whitespace-nowrap'
                      }`}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        setContextMenu({
                          isOpen: true,
                          position: { x: e.clientX, y: e.clientY },
                          type: 'cell',
                          data: { 
                            column,
                            transaction,
                            value: getCellValue(transaction, column.field)
                          }
                        });
                      }}
                    >
                      {renderCell(transaction, column.field)}
                      {column.field === 'type' && editingTypeId === transaction._id && (
                        <div className="relative" style={{ position: 'static' }}>
                          <div className="absolute right-0 top-full z-40" style={{ minWidth: '160px' }}>
                            <TypeSelector
                              transactionId={transaction._id}
                              currentType={transaction.type || (transaction.amount >= 0 ? 'income' : 'expense')}
                              onSelectType={updateTransactionType}
                              onCancel={() => setEditingTypeId(null)}
                            />
                          </div>
                        </div>
                      )}
                      {column.field === 'purchaser' && editingOwnerId === transaction._id && (
                        <div className="relative" style={{ position: 'static' }}>
                          <div className="absolute left-0 top-full z-40" style={{ minWidth: '192px' }}>
                            <OwnerSelector
                              transactionId={transaction._id}
                              currentOwnerType={transaction.editableOwner || transaction.ownerType || 'personal'}
                              user={user}
                              onSelectOwner={updateTransactionOwner}
                              onCancel={() => setEditingOwnerId(null)}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                  );
                  })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls - Only show if using client-side pagination */}
        {!useServerPagination && totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredTransactions.length)}</span> of{' '}
              <span className="font-medium">{filteredTransactions.length}</span> transactions
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Bulk Edit Modal */}
        <BulkEditModal
          isOpen={isBulkEditModalOpen}
          onClose={() => setIsBulkEditModalOpen(false)}
          selectedTransactionIds={selectedTransactionIds}
          categories={categories}
          user={user}
          onSuccess={() => {
            // Clear selection and refresh
            setSelectedTransactionIds([]);
            setSelectAll(false);
            onTransactionUpdate && onTransactionUpdate();
          }}
        />

        {/* Column Customization Modal */}
        <ColumnCustomizationModal
          isOpen={isColumnModalOpen}
          onClose={() => setIsColumnModalOpen(false)}
          columns={allAvailableColumns}
          onSave={async (preferences) => {
            try {
              console.log('💾 Saving column preferences:', preferences);
              const savedPreferences = await userPreferencesService.saveTransactionColumnPreferences(preferences);
              console.log('✅ Saved preferences response:', savedPreferences);
              // Use the response from server to ensure consistency
              setColumnPreferences(savedPreferences || preferences);
            } catch (error) {
              console.error('❌ Error saving preferences:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          savedPreferences={columnPreferences}
        />

        {/* Context Menu */}
        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          onClose={() => setContextMenu({ isOpen: false, position: { x: 0, y: 0 }, type: null, data: null })}
          items={(() => {
            if (contextMenu.type === 'header') {
              const { column } = contextMenu.data || {};
              if (!column) return [];
              
              const isCurrentlySorted = sortField === column.field;
              const currentDirection = sortDirection;
              
              return [
                {
                  id: 'sort-asc',
                  label: 'Sort A-Z',
                  icon: <BarsArrowUpIcon className="h-4 w-4" />,
                  onClick: () => {
                    setSortField(column.field);
                    setSortDirection('asc');
                    toast.success(`Sorted by ${column.label} (A-Z)`);
                  }
                },
                {
                  id: 'sort-desc',
                  label: 'Sort Z-A',
                  icon: <BarsArrowDownIcon className="h-4 w-4" />,
                  onClick: () => {
                    setSortField(column.field);
                    setSortDirection('desc');
                    toast.success(`Sorted by ${column.label} (Z-A)`);
                  }
                },
                { divider: true },
                {
                  id: 'customize-columns',
                  label: 'Customize Columns',
                  icon: <Cog6ToothIcon className="h-4 w-4" />,
                  onClick: () => {
                    // onColumnCustomizeClick is a ref, so check if it has a current function
                    if (onColumnCustomizeClick && typeof onColumnCustomizeClick.current === 'function') {
                      onColumnCustomizeClick.current();
                    } else {
                      setIsColumnModalOpen(true);
                    }
                  }
                }
              ];
            } else if (contextMenu.type === 'cell') {
              const { column, transaction, value } = contextMenu.data || {};
              if (!column || !transaction) return [];
              
              const menuItems = [
                {
                  id: 'sort',
                  label: `Sort by ${column.label}`,
                  icon: <BarsArrowUpIcon className="h-4 w-4" />,
                  onClick: () => {
                    setSortField(column.field);
                    setSortDirection('asc');
                    toast.success(`Sorted by ${column.label}`);
                  }
                }
              ];
              
              // Add "Filter Out" option (exclude this value)
              if (value !== null && value !== undefined && value !== '') {
                menuItems.push({
                  id: 'filter-out',
                  label: `Filter Out "${String(value).substring(0, 30)}${String(value).length > 30 ? '...' : ''}"`,
                  icon: <FunnelIcon className="h-4 w-4" />,
                  onClick: () => {
                    if (onFilterChange) {
                      // Create a filter to exclude this value
                      const newFilter = { ...filters };
                      
                      // Map column field to filter key
                      const filterKeyMap = {
                        'category': 'category',
                        'type': 'type',
                        'account': 'accounts',
                        'source': 'source',
                        'purchaser': 'owner',
                        'shared': 'shared',
                        'merchant': 'merchant',
                        'recurring': 'recurring'
                      };
                      
                      const filterKey = filterKeyMap[column.field];
                      if (filterKey) {
                        // For now, just show a message - full "filter out" would need more complex logic
                        toast.info(`Filter out functionality coming soon for ${column.label}`);
                        // TODO: Implement actual filter-out logic
                      } else {
                        toast.info(`Filter out not available for ${column.label}`);
                      }
                    } else {
                      toast.info('Filter functionality not available');
                    }
                  }
                });
                
                // Add "Show All Like" option (filter to show only this value)
                menuItems.push({
                  id: 'show-all-like',
                  label: `Show All Like "${String(value).substring(0, 30)}${String(value).length > 30 ? '...' : ''}"`,
                  icon: <EyeIcon className="h-4 w-4" />,
                  onClick: () => {
                    if (onFilterChange) {
                      const newFilter = { ...filters };
                      
                      // Map column field to filter key and value
                      const filterKeyMap = {
                        'category': 'category',
                        'type': 'type',
                        'account': 'accounts',
                        'source': 'source',
                        'purchaser': 'owner',
                        'shared': 'shared',
                        'merchant': 'merchant',
                        'recurring': 'recurring'
                      };
                      
                      const filterKey = filterKeyMap[column.field];
                      if (filterKey) {
                        if (filterKey === 'category') {
                          // For category, find the category ID
                          const categoryId = transaction.category?._id || transaction.categoryRef?._id;
                          if (categoryId) {
                            newFilter.category = [categoryId];
                            onFilterChange(newFilter);
                            toast.success(`Showing only ${column.label}: ${value}`);
                          } else {
                            toast.error('Category ID not found');
                          }
                        } else if (filterKey === 'accounts') {
                          // For accounts, find the account ID
                          const accountId = transaction.account?._id || transaction.wallet?._id;
                          if (accountId) {
                            newFilter.accounts = [accountId];
                            onFilterChange(newFilter);
                            toast.success(`Showing only ${column.label}: ${value}`);
                          } else {
                            toast.error('Account ID not found');
                          }
                        } else {
                          // For other fields, set the filter value directly
                          newFilter[filterKey] = value;
                          onFilterChange(newFilter);
                          toast.success(`Showing only ${column.label}: ${value}`);
                        }
                      } else {
                        toast.info(`Filter not available for ${column.label}`);
                      }
                    } else {
                      toast.info('Filter functionality not available');
                    }
                  }
                });
              }
              
              return menuItems;
            }
            return [];
          })()}
        />
      </div>
    </ErrorBoundary>
  );
};

// Memoize TransactionList to prevent unnecessary re-renders
// Only re-renders when props actually change
export default memo(TransactionList); 